import { Check, ChevronDown } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

// Dropdown and tooltip drawn by the application, never by the operating system: same surface,
// border, radius and shadow in both themes. Both live in a portal so a table or a panel with
// `overflow: hidden` never clips them.

export interface SelectOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface Place {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  above: boolean;
}

function placeBelow(anchor: HTMLElement, minWidth: number): Place {
  const rect = anchor.getBoundingClientRect();
  const width = Math.max(rect.width, minWidth);
  const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
  const below = window.innerHeight - rect.bottom - 12;
  const above = rect.top - 12;
  const flip = below < 180 && above > below;
  return { left, top: flip ? rect.top - 4 : rect.bottom + 4, width, maxHeight: Math.min(320, flip ? above : below), above: flip };
}

export function Select<T extends string>({ value, options, onChange, label, disabled, minWidth = 160 }: { value: T; options: Array<SelectOption<T>>; onChange: (value: T) => void; label: string; disabled?: boolean; minWidth?: number }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [place, setPlace] = useState<Place | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const id = useId();
  const selected = options.findIndex((option) => option.value === value);

  const close = useCallback((focus = true) => {
    setOpen(false);
    if (focus) button.current?.focus();
  }, []);

  const show = () => {
    if (disabled || !button.current) return;
    setActive(Math.max(0, selected));
    setPlace(placeBelow(button.current, minWidth));
    setOpen(true);
  };

  const pick = (index: number) => {
    const option = options[index];
    if (option && option.value !== value) onChange(option.value);
    close();
  };

  useLayoutEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!list.current?.contains(target) && !button.current?.contains(target)) close(false);
    };
    const onMove = (event: Event) => {
      if (!list.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, close]);

  const onKey = (event: KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        show();
      }
      return;
    }
    const last = options.length - 1;
    if (event.key === 'ArrowDown') setActive((index) => Math.min(last, index + 1));
    else if (event.key === 'ArrowUp') setActive((index) => Math.max(0, index - 1));
    else if (event.key === 'Home') setActive(0);
    else if (event.key === 'End') setActive(last);
    else if (event.key === 'Enter' || event.key === ' ') pick(active);
    else if (event.key === 'Escape' || event.key === 'Tab') {
      if (event.key === 'Escape') event.stopPropagation();
      close(event.key === 'Escape');
      if (event.key === 'Tab') return;
    } else if (event.key.length === 1) {
      const letter = event.key.toLowerCase();
      const found = options.findIndex((option, index) => index > active && String(textOf(option.label)).toLowerCase().startsWith(letter));
      const wrap = found >= 0 ? found : options.findIndex((option) => String(textOf(option.label)).toLowerCase().startsWith(letter));
      if (wrap >= 0) setActive(wrap);
    } else return;
    event.preventDefault();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`select${open ? ' open' : ''}`}
        disabled={disabled}
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close() : show())}
        onKeyDown={onKey}
      >
        <span className="select-value">{options[selected]?.label ?? '—'}</span>
        <ChevronDown size={14} className="select-caret" />
      </button>
      {open && place
        ? createPortal(
            <div
              ref={list}
              id={id}
              role="listbox"
              aria-label={label}
              className={`select-menu${place.above ? ' above' : ''}`}
              style={{ left: place.left, top: place.top, minWidth: place.width, maxHeight: place.maxHeight }}
            >
              {options.map((option, index) => (
                <div
                  key={option.value}
                  data-index={index}
                  role="option"
                  aria-selected={index === selected}
                  className={`select-option${index === active ? ' active' : ''}${index === selected ? ' on' : ''}`}
                  onPointerMove={() => setActive(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => pick(index)}
                >
                  <span>{option.label}</span>
                  {index === selected ? <Check size={14} /> : null}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function textOf(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  return '';
}

/**
 * One tooltip for the whole application: any element with `data-tip` shows it on hover or focus.
 * A `title` left on an element (third-party markup, future code) is moved to `data-tip` on the
 * spot, so the system tooltip never appears.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<{ text: string; x: number; y: number; below: boolean } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const current = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const find = (target: EventTarget | null) => {
      if (!(target instanceof Element)) return null;
      const titled = target.closest<HTMLElement>('[title]');
      if (titled && titled.getAttribute('title')) {
        titled.setAttribute('data-tip', titled.getAttribute('title')!);
        titled.removeAttribute('title');
      }
      return target.closest<HTMLElement>('[data-tip]');
    };
    const hide = () => {
      window.clearTimeout(timer.current);
      current.current = null;
      setTip(null);
    };
    const enter = (target: EventTarget | null, delay: number) => {
      const element = find(target);
      if (element === current.current) return;
      hide();
      const text = element?.getAttribute('data-tip');
      if (!element || !text) return;
      current.current = element;
      timer.current = window.setTimeout(() => {
        if (!element.isConnected) return;
        const rect = element.getBoundingClientRect();
        const below = rect.top < 48;
        setTip({ text, x: rect.left + rect.width / 2, y: below ? rect.bottom + 6 : rect.top - 6, below });
      }, delay);
    };
    const over = (event: PointerEvent) => enter(event.target, 350);
    const focus = (event: FocusEvent) => {
      if ((event.target as Element | null)?.matches?.(':focus-visible')) enter(event.target, 0);
    };
    document.addEventListener('pointerover', over);
    document.addEventListener('focusin', focus);
    document.addEventListener('focusout', hide);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('keydown', hide, true);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.clearTimeout(timer.current);
      document.removeEventListener('pointerover', over);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('keydown', hide, true);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);

  const box = useRef<HTMLDivElement>(null);
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!tip || !box.current) return setShift(0);
    const rect = box.current.getBoundingClientRect();
    const overflowRight = rect.right - (window.innerWidth - 8);
    const overflowLeft = 8 - rect.left;
    setShift(overflowRight > 0 ? -overflowRight : overflowLeft > 0 ? overflowLeft : 0);
  }, [tip]);

  if (!tip) return null;
  return createPortal(
    <div ref={box} role="tooltip" className={`tip${tip.below ? ' below' : ''}`} style={{ left: tip.x + shift, top: tip.y }}>
      {tip.text}
    </div>,
    document.body,
  );
}
